import React from "react";
import { Box, Typography, Paper, alpha } from "@mui/material";
import { colors } from "../../../styles/colors";
import InfoTooltip from "../overlays/InfoTooltip";

export interface HeroKPIProps {
  /** Titel der KPI */
  title: string;
  /** Formatierter Wert (z.B. "16,0 %") */
  value: string;
  /** Icon-Element (z.B. <TrendingUpIcon />) */
  icon: React.ReactNode;
  /** Ausgeschriebene Bezeichnung (z.B. "Internal Rate of Return") */
  subtitle?: string;
  /** Kurze Beschreibung was die Kennzahl aussagt */
  description?: string;
}

/**
 *
 * Moderne, minimalistische KPI-Card mit:
 * - Farbigem Icon
 * - Großer Wert-Anzeige
 * - Subtiler Hintergrund mit Farbverlauf
 *
 * @example
 * ```tsx
 * <HeroKPI
 *   title="Eigenkapital IRR"
 *   value="16,0 %"
 *   icon={<TrendingUpIcon sx={{ fontSize: 24 }} />}
 *   subtitle="Internal Rate of Return"
 * />
 * ```
 */
const HeroKPI: React.FC<HeroKPIProps> = ({
  title,
  value,
  icon,
  subtitle,
  description,
}) => {
  const hasTooltip = subtitle || description;

  // Tooltip-Content zusammenbauen
  const tooltipContent = hasTooltip ? (
    <Box>
      {subtitle && (
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 600,
            mb: description ? 0.5 : 0,
          }}
        >
          {subtitle}
        </Typography>
      )}
      {description && (
        <Typography
          sx={{
            fontSize: "0.7rem",
            lineHeight: 1.5,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  ) : (
    ""
  );

  return (
    <Box sx={{ position: "relative" }}>
      <Paper
        elevation={0}
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: "16px",
          background: colors.background.paper,
          boxShadow: `0 4px 12px ${colors.shadow.light}`,
          transition: "box-shadow 0.2s ease-in-out",
          "&:hover": {
            boxShadow: `0 4px 16px ${colors.shadow.medium}`,
          },
        }}
      >
        <Box sx={{ p: 2.5, position: "relative", zIndex: 1 }}>
          {/* Horizontales Layout: Icon links, Content rechts */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* Icon */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 44,
                height: 44,
                borderRadius: "12px",
                background: `linear-gradient(135deg, ${alpha(colors.brand.primary, 0.05)} 0%, ${alpha(colors.brand.primary, 0.02)} 100%)`,
                color: colors.brand.primary,
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Titel mit InfoTooltip */}
              {hasTooltip ? (
                <InfoTooltip title={tooltipContent} placement="top">
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 500,
                      fontSize: "0.75rem",
                      display: "block",
                      mb: 0.25,
                    }}
                  >
                    {title}
                  </Typography>
                </InfoTooltip>
              ) : (
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    display: "block",
                    mb: 0.25,
                  }}
                >
                  {title}
                </Typography>
              )}

              {/* Wert */}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 500,
                  color: "text.primary",
                  fontSize: "1.75rem",
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                }}
              >
                {value}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default HeroKPI;
